import React, { useState, useEffect } from 'react';
import {array, bool, func, object, string} from 'prop-types';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import _ from 'lodash';

import {connect} from 'react-redux';

import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';
import AutoComplete from '../../components/FormFields/Autocomplete';
import SensorWarningModal from './SensorWarningModal';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as BasAgentActions from '../../actions/basAgentActions';
import * as DeviceActions from '../../actions/deviceActions';
import * as UiActions from '../../actions/uiActions';

import classes from './EditEntityModal.module.css';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(100)
        .required(),
    parent: Yup.string()
        .required(),
    agent: Yup.string()
        .required(),
    device: Yup.string()
        .required(),
    deviceObject: Yup.string()
        .required(),
    dataType: Yup.string()
        .required(),
    sensorType: Yup.object()
        .required(),
    rawUnit: Yup.object()
        .when('dataType', {
            is:        'boolean',
            then:      Yup.object().notRequired(),
            otherwise: Yup.object().required()
        }),
    displayUnit: Yup.object()
        .when('dataType', {
            is:        'boolean',
            then:      Yup.object().notRequired(),
            otherwise: Yup.object().required()
        }),
    displayDataType: Yup.string(),
    conversion:      Yup.string()
        .nullable()
});

const DEFAULT_FORM_VALUES = {
    name:            '',
    parent:          '',
    agent:           '',
    device:          '',
    deviceObject:    '',
    dataType:        '',
    sensorType:      '',
    rawUnit:         {},
    displayUnit:     {},
    displayDataType: '',
    conversion:      ''
};

const InitialValues = {...DEFAULT_FORM_VALUES};

const EditSensorModal = ({
    agents,
    clearErrors,
    close,
    conversions,
    deviceObjects,
    devices,
    errors,
    fetchDeviceObjects,
    isLoading,
    open,
    selectedNode,
    selectedSensor,
    sensorTypes,
    title,
    toggleLoading,
    units,
    ...props
}) => {
    const [displayUnits, setDisplayUnits] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [loadingDeviceObjects, setLoadingDeviceObjects] = useState(false);
    const [filteredConversions, setFilteredConversions] = useState([]);
    const [unitTypeDisabled, setUnitTypeDisabled] = useState(false);
    const [filteredAgentList, setFilteredAgentList] = useState([]);
    const [activeAgent, setActiveAgent] = useState({});
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    const [warningFunction, setWarningFunction] = useState(false);

    useEffect(() => {
        if (!_.isEmpty(selectedSensor)) {
            const deviceId = _.get(selectedSensor, 'deviceId', '');

            if (!selectedSensor.calculatedSensor && deviceId !== EMPTY_GUID) {
                fetchDeviceObjects(deviceId);
            }
        }
    }, [selectedSensor]);

    useEffect(() => {
        if (!_.isEmpty(conversions)) {
            const deviceObjectId = _.get(selectedSensor, 'deviceObjectId', '');

            setFilteredConversions(refineConversions(deviceObjectId));
        }
    }, [conversions]);

    useEffect(() => {
        if (!_.isEmpty(deviceObjects)) {
            InitialValues.dataType = getDataType(_.get(selectedSensor, 'deviceObjectId', ''));
        }
    }, [deviceObjects]);

    useEffect(() => {
        if (selectedNode && (selectedNode.type === 'equipmentItem' || selectedNode.type === 'systemItem')) {
            if (selectedNode.activeNode && selectedNode.activeNode.siteId) {
                const siteId = selectedNode.activeNode.siteId;
                const siteNotNull = _.cloneDeep(agents.filter(a => a.site !== null));
                const list = siteNotNull.filter(s => {
                    if (s.site?.find(site => site.id === siteId)) {
                        return true;
                    }
                    else {
                        return false;
                    }
                });
                setFilteredAgentList(list);
            }
            
        }
    }, [selectedNode]);
    
    const formik = useFormik({
        initialValues:      InitialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                companyInfo,
                updateSensor
            } = props;

            let isUpdated;

            const parents = selectedNode.parent.split('/');
            const siteId = parents[parents.length - 2];

            const body = {
                name:               values.name,
                sensorTypeId:       values.sensorType.id,
                rawUnitId:          values.rawUnit.id,
                presentationUnitId: values.displayUnit.id,
                equipmentId:        '',
                systemId:           '',
                deviceObjectId:     values.deviceObject,
                deviceId:           values.device,
                agentId:            values.agent,
                conversionId:       values.conversion === 'No Conversion' ? '' : values.conversion
            };

            if (selectedNode.type === 'equipmentItem') {
                body.equipmentId = selectedNode.id;
            } else if (selectedNode.type === 'systemItem') {
                body.systemId = selectedNode.id;
            }

            clearErrors();
            setSubmitting(true);

            const handleSubmit = async () => {
                if (!isLoading) {
                    toggleLoading(true);
                    isUpdated = await updateSensor(selectedSensor.id, selectedNode.id, siteId, companyInfo, body);
                }

                if (isUpdated) {
                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            };

            if (activeAgent.sensorCount >= 4000) {
                setWarningModalOpen(true);
                setWarningFunction(() => () => handleSubmit());
                return;
            }

            try {
                handleSubmit();
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const getUnit = unitId => {
        let targetUnit;

        units.forEach(unit => {
            unit.items.forEach(item => {
                if (item.id === unitId) {
                    targetUnit = {...item};
                }
            });
        });

        return targetUnit;
    };

    const getDataType = deviceObjectId => {
        const deviceObject = _.find(deviceObjects, item => item.id === deviceObjectId);
        return _.get(deviceObject, 'dataType', '').toLowerCase();
    };

    const prepareDropdowns = async () => {
        const {
            companyInfo,
            fetchBasAgent,
            fetchConversions,
            fetchDevice,
            fetchDevicesList,
            setAgent
        } = props;

        const agentId = _.get(selectedSensor, 'agentId', '');
        const deviceId = _.get(selectedSensor, 'deviceId', '');
        const companyId = _.get(companyInfo, 'companyId', '');

        setAgent(null);
        const selectedAgent = await fetchBasAgent(agentId);
        setActiveAgent(selectedAgent);

        const configurationId = _.get(selectedAgent, 'driverConfiguration[0].id', '');

        await Promise.all([
            fetchDevice(deviceId),
            fetchDevicesList(agentId, configurationId),
            fetchDeviceObjects(deviceId),
            fetchConversions(companyId)
        ].map(p => p.catch(err => err))).then(() => {
            toggleLoading(false);
        });
    };

    const refineConversions = deviceObjectId => {
        const dataType = getDataType(deviceObjectId);
        return conversions.filter(conversion => conversion.rawDataType === dataType);
    };

    const getSensorType = id => {
        if (_.isEmpty(id)) return {};

        return sensorTypes[0].items.find(type => type.id === id);
    };

    const handleReset = async () => {
        if (InitialValues.name.length) {
            Object.assign(InitialValues, DEFAULT_FORM_VALUES);
        }

        const linked = _.get(selectedSensor, 'agentId', '') !== EMPTY_GUID;

        if (linked) {
            toggleLoading(true);

            await prepareDropdowns();
        }

        InitialValues.name = _.get(selectedSensor, 'name', '');
        InitialValues.parent = _.get(selectedNode, 'name', '');
        InitialValues.sensorType = getSensorType(_.get(selectedSensor, 'sensorType.id', ''));
        InitialValues.displayDataType = _.get(selectedSensor, 'displayDataType', '');

        if (InitialValues.displayDataType !== 'float') {
            setUnitTypeDisabled(true);
        } else {
            setUnitTypeDisabled(false);

            const rawUnitId = _.get(selectedSensor, 'rawUnitId', '');
            const displayUnitId = _.get(selectedSensor, 'presentationUnitId', rawUnitId);

            if (rawUnitId.length) {
                InitialValues.rawUnit = getUnit(rawUnitId);
            }

            if (displayUnitId.length) {
                InitialValues.displayUnit = getUnit(displayUnitId);
            }

            let displayUnits = [];
            units.forEach(group => {
                if (group.name === InitialValues.rawUnit.unitType) {
                    displayUnits = [group];
                    return;
                }
            });

            setDisplayUnits(displayUnits);
        }

        if (linked) {
            InitialValues.agent = _.get(selectedSensor, 'agentId', '');
            InitialValues.device = _.get(selectedSensor, 'deviceId', '');
            InitialValues.deviceObject = _.get(selectedSensor, 'deviceObjectId', '');

            const conversionId = _.get(selectedSensor, 'conversionId', '');

            if (conversionId && conversionId.length) {
                InitialValues.conversion = conversionId;
            } else {
                InitialValues.conversion = '';
            }
        }

        formik.handleReset(InitialValues);
    };

    const handleSensorTypeChange = event => {
        const {value} = event.target;

        if (formik.values.name) {
            formik.handleChange(event);

            return;
        }

        formik.setFieldValue('name', value.name, false);

        formik.handleChange(event);
    };

    const handleAgentChange = async event => {
        setLoadingDevices(true);
        const {fetchDevicesList, fetchBasAgent, setAgent, setDeviceObjects} = props;
        const {value} = event.target;

        if (value !== formik.values.agent) {
            formik.setFieldValue('device', '', false);
            formik.setFieldValue('deviceObject', '', false);

            formik.setTouched({
                device:       false,
                deviceObject: false
            });

            setAgent(null);
            const selectedAgent = await fetchBasAgent(value);
            setActiveAgent(selectedAgent);

            const configurationId = _.get(selectedAgent, 'driverConfiguration[0].id', '');

            await fetchDevicesList(value, configurationId);
            setDeviceObjects([]);
        }

        toggleLoading(false);

        formik.handleChange(event);
        setLoadingDevices(false);
    };

    const handleDeviceChange = async event => {
        setLoadingDeviceObjects(true);
        const {value} = event.target;

        if (value !== formik.values.device) {
            formik.setFieldValue('deviceObject', '', false);

            formik.setTouched({
                deviceObject: false
            });

            await fetchDeviceObjects(value);
        }

        toggleLoading(false);

        formik.handleChange(event);
        setLoadingDeviceObjects(false);
    };

    const handleDeviceObjectChange = event => {
        const {value} = event.target;

        const deviceObject = _.find(deviceObjects, item => item.id === value);
        const dataType = _.get(deviceObject, 'dataType', '').toLowerCase();

        setFilteredConversions(refineConversions(value));

        const conversionIsEmpty = _.chain(filteredConversions).find(conversion => conversion.id === formik.values.conversion).isEmpty().value();

        if (_.isEmpty(formik.values.conversion) || conversionIsEmpty) {
            formik.setFieldValue('displayDataType', dataType, false);

            formik.setFieldValue('conversion', '', false);
        }

        formik.setFieldValue('dataType', dataType, false);

        formik.setFieldValue('rawUnit', '', false);
        formik.setFieldValue('displayUnit', '', false);

        if (dataType !== 'float') {
            setUnitTypeDisabled(true);

            formik.setFieldValue('unit', '', false);
        } else {
            setUnitTypeDisabled(false);
        }

        toggleLoading(false);

        formik.handleChange(event);
    };

    const handleConversionChange = event => {
        const { value } = event.target;
        let targetConversion;

        const conversion = _.find(conversions, item => item.id === value);

        if (value === 'No Conversion' || !value.length) {
            targetConversion = getDataType(_.get(selectedSensor, 'deviceObjectId', ''));
        } else {
            targetConversion = _.get(conversion, 'displayDataType', '');
        }

        if (value === 'No Conversion' || !value.length || _.get(conversion, 'displayDataType', '') === 'float') {

            const rawUnitId = _.get(selectedSensor, 'rawUnitId', '');
            const displayUnitId = _.get(selectedSensor, 'presentationUnitId', rawUnitId);

            if (!_.isNil(rawUnitId) && rawUnitId.length) {
                formik.setFieldValue('rawUnit', getUnit(rawUnitId), false);
            }
            else {
                formik.setFieldValue('rawUnit', null);
            }

            if (!_.isNil(displayUnitId) && displayUnitId.length) {
                formik.setFieldValue('displayUnit', getUnit(displayUnitId), false);
            }
            else {
                formik.setFieldValue('displayUnit', null);
            }

            let displayUnits = [];
            units.forEach(group => {
                if (group.name === InitialValues.rawUnit.unitType) {
                    displayUnits.push(group);
                    return;
                }
            });

            setDisplayUnits(displayUnits);
        }

        if (targetConversion === 'float') {
            setUnitTypeDisabled(false);
        } else {
            setUnitTypeDisabled(true);
            formik.setFieldValue('rawUnit', {}, false);
            formik.setFieldValue('displayUnit', {}, false);
        }

        formik.setFieldValue('displayDataType', targetConversion, false);

        toggleLoading(false);

        formik.handleChange(event);
    };

    const handleRawUnitChange = event => {
        const { value } = event.target;
        let displayUnits;

        toggleLoading(false);

        units.forEach(group => {
            if (group.name === value.unitType) {
                displayUnits = [group];
                return;
            }
        });

        setDisplayUnits(displayUnits);

        formik.handleChange(event);
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Update"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/sensorTreeIcon.svg" alt="Sensor Icon" />
                        <span>Edit Sensor</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select a Sensor Type.
                        </p>
                        <p>
                            Enter a Sensor name.
                        </p>
                        <p>
                            Select an Agent configured in IoT Hub.
                        </p>
                        <p>
                            Choose a Device from the selected Agent.
                        </p>
                        <p>
                            Choose a Device Object from the selected Device.
                        </p>
                        <p>
                            If no Agents, Devices or Device Objects are available, please configure those entities in IoT Hub.
                        </p>
                        <p>
                            If the Data Type is Float, you must specify a Unit. If the Data Type is Boolean, String or Integer, Units are not required.
                        </p>
                        <p>
                            Select a Conversion.
                        </p>
                        <p>
                            Note: Conversions are not required. Please see the help docs for more information on Conversions.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <AutoComplete
                            grouped
                            label="Sensor Type"
                            name="sensorType"
                            onBlur={formik.handleBlur}
                            onChange={handleSensorTypeChange}
                            options={sensorTypes}
                            placeholder="Sensor Type"
                            value={formik.values.sensorType}
                            error={Boolean(formik.errors.sensorType) && Boolean(formik.touched.sensorType)}
                            helperText={Boolean(formik.touched.sensorType) && formik.errors.sensorType}
                        />
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Sensor Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                disabled
                                id="parent"
                                name="parent"
                                type="text"
                                value={formik.values.parent}
                                label="Parent"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.parent) && Boolean(formik.touched.parent)}
                                helperText={Boolean(formik.touched.parent) && formik.errors.parent}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="agent"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.agent) && Boolean(formik.touched.agent)}
                            >
                                Agent
                            </InputLabel>
                            <Select
                                name="agent"
                                id="agent"
                                value={formik.values.agent}
                                onChange={handleAgentChange}
                                classes={{
                                    root: classes.select
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.agent) && Boolean(formik.touched.agent)}
                            >
                                {
                                    _.orderBy(filteredAgentList, [agent => agent.name.toLowerCase()], ['asc']).map(agent =>
                                        <MenuItem
                                            key={agent.id}
                                            value={agent.id}
                                        >
                                            {agent.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.agent &&
                             formik.errors.agent && <FormHelperText className={classes.error}>{formik.errors.agent}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="device"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.device) && Boolean(formik.touched.device)}
                            >
                                Device
                            </InputLabel>
                            <Select
                                name="device"
                                id="device"
                                disabled={!formik.values.agent || loadingDevices}
                                value={formik.values.device}
                                onChange={handleDeviceChange}
                                classes={{
                                    root: classes.select
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        {loadingDevices ?
                                            <CircularProgress className={classes.adornment} size={14} />
                                            :
                                            null
                                        }
                                    </InputAdornment>
                                }
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.device) && Boolean(formik.touched.device)}
                            >
                                {
                                    _.isEmpty(devices) ?
                                        <MenuItem
                                            key={'start'}
                                            disabled={true}
                                            value={'none'}
                                        >
                                            Agent has no devices
                                        </MenuItem>
                                        :
                                        null
                                }
                                {
                                    devices.map(deviceItem =>
                                        <MenuItem
                                            key={deviceItem.id}
                                            value={deviceItem.id}
                                        >
                                            {deviceItem.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.device &&
                             formik.errors.device && <FormHelperText className={classes.error}>{formik.errors.device}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="deviceObject"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.deviceObject) && Boolean(formik.touched.deviceObject)}
                            >
                                Device Object
                            </InputLabel>
                            <Select
                                name="deviceObject"
                                id="deviceObject"
                                disabled={!formik.values.device || loadingDeviceObjects}
                                value={formik.values.deviceObject}
                                onChange={handleDeviceObjectChange}
                                classes={{
                                    root: classes.select
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        {loadingDeviceObjects ?
                                            <CircularProgress className={classes.adornment} size={14} />
                                            :
                                            null
                                        }
                                    </InputAdornment>
                                }
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.deviceObject) && Boolean(formik.touched.deviceObject)}
                            >
                                {
                                    _.isEmpty(deviceObjects) ?
                                        <MenuItem
                                            key={'start'}
                                            disabled={true}
                                            value={'none'}
                                        >
                                            Device has no objects
                                        </MenuItem>
                                        :
                                        null
                                }
                                {
                                    deviceObjects.map(objectItem =>
                                        <MenuItem
                                            key={objectItem.id}
                                            value={objectItem.id}
                                        >
                                            {objectItem.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.deviceObject &&
                             formik.errors.deviceObject && <FormHelperText className={classes.error}>{formik.errors.deviceObject}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                disabled
                                id="dataType"
                                name="dataType"
                                type="text"
                                value={formik.values.dataType}
                                label="Data Type"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.dataType) && Boolean(formik.touched.dataType)}
                                helperText={Boolean(formik.touched.dataType) && formik.errors.dataType}
                            />
                        </FormControl>
                        <AutoComplete
                            grouped
                            disabled={unitTypeDisabled}
                            label="Raw Unit"
                            name="rawUnit"
                            onBlur={formik.handleBlur}
                            onChange={handleRawUnitChange}
                            options={units}
                            placeholder="Raw Unit"
                            value={formik.values.rawUnit}
                            error={Boolean(formik.errors.rawUnit)}
                            helperText={formik.errors.rawUnit}
                        />
                        <AutoComplete
                            grouped
                            disabled={unitTypeDisabled}
                            label="Display Unit"
                            name="displayUnit"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            options={displayUnits}
                            placeholder="Display Unit"
                            value={formik.values.displayUnit}
                            error={Boolean(formik.errors.displayUnit)}
                            helperText={formik.errors.displayUnit}
                        />
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                disabled
                                id="displayDataType"
                                name="displayDataType"
                                type="text"
                                value={formik.values.displayDataType}
                                label="Display Data Type"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.displayDataType) && Boolean(formik.touched.displayDataType)}
                                helperText={Boolean(formik.touched.displayDataType) && formik.errors.displayDataType}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="conversion"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.conversion) && Boolean(formik.touched.conversion)}
                            >
                                Conversion
                            </InputLabel>
                            <Select
                                name="conversion"
                                id="conversion"
                                value={formik.values.conversion}
                                onChange={handleConversionChange}
                                classes={{
                                    root: classes.select
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.conversion) && Boolean(formik.touched.conversion)}
                            >
                                <MenuItem
                                    key={'No Conversion'}
                                    value={'No Conversion'}
                                >
                                    No Conversion
                                </MenuItem>
                                {
                                    filteredConversions.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.conversion &&
                             formik.errors.conversion && <FormHelperText className={classes.error}>{formik.errors.conversion}</FormHelperText>}
                        </FormControl>
                    </div>
                </div>
            </div>
            {
                warningModalOpen ?
                    <SensorWarningModal
                        warningModalOpen={warningModalOpen}
                        setWarningModalOpen={setWarningModalOpen}
                        handleReset={formik.resetForm}
                        handleSubmit={warningFunction}
                    />
                    :
                    null
            }
        </EsmsDialog>
    );
};

EditSensorModal.defaultProps = {
    agents:         [],
    close:          () => {},
    conversions:    [],
    devices:        [],
    deviceObjects:  [],
    open:           false,
    selectedNode:   {},
    selectedDevice: {},
    selectedSensor: {},
    sensorTypes:    [],
    title:          '',
    units:          []
};

EditSensorModal.propTypes = {
    agents:         array,
    close:          func,
    conversions:    array,
    devices:        array,
    deviceObjects:  array,
    open:           bool,
    selectedNode:   object,
    selectedDevice: object,
    selectedSensor: object,
    sensorTypes:    array,
    title:          string,
    units:          array
};

const mapStateToProps = ({ basAgent, ...state }) => {
    const { basAgents } = basAgent;

    return {
        agents:         Object.values(basAgents),
        companyInfo:    state.companyInfo,
        conversions:    state.architect.conversions,
        devices:        state.device.devices,
        deviceObjects:  state.device.deviceObjects,
        errors:         state.messages.errors,
        isLoading:      state.ui.isLoading,
        selectedDevice: state.device.selectedDevice,
        selectedNode:   state.architect.selectedNode,
        selectedSensor: state.architect.selectedSensor,
        sensorTypes:    state.sensorTypes.data,
        units:          state.architect.rawUnits
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...BasAgentActions,
    ...DeviceActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EditSensorModal);
